/**
 * Workspace Types
 *
 * TypeScript interfaces and types for workspace multi-tenant system
 */

// =====================================================================
// PERMISSIONS
// =====================================================================

export interface WorkspacePermissions {
  can_view_analytics: number; // 0 or 1
  can_manage_agents: number;
  can_manage_workspace: number; // Consolidates can_manage_groups + can_manage_members
  can_admin: number;
}

export const DEFAULT_PERMISSIONS: WorkspacePermissions = {
  can_view_analytics: 0,
  can_manage_agents: 0,
  can_manage_workspace: 0,
  can_admin: 0,
};

export const ADMIN_PERMISSIONS: WorkspacePermissions = {
  can_view_analytics: 1,
  can_manage_agents: 1,
  can_manage_workspace: 1,
  can_admin: 1,
};

// =====================================================================
// WORKSPACE SETTINGS
// =====================================================================

export interface WorkspaceSettings {
  id: number;
  workspace_id: number;
  is_official_td_workspace: boolean;
  can_publish_global_agents: boolean;
  can_access_all_workspaces: boolean;
  can_manage_all_workspaces: boolean;
  custom_settings?: Record<string, any>;
}

// =====================================================================
// WORKSPACE
// =====================================================================

export interface Workspace {
  id: number;
  name: string;
  owner_id: number;
  monthly_token_limit: number;
  token_used: number;
  is_td_workspace: boolean;
  is_owner: boolean; // True if current user is the workspace owner, false if just a member
  created_at: string;
  updated_at: string;
  settings?: WorkspaceSettings; // Optional workspace settings (loaded when needed)
}

export interface WorkspaceStats {
  total_members: number;
  total_groups: number;
  total_agents: number;
  token_usage_percentage: number;
}

export interface WorkspaceWithStats extends Workspace {
  stats?: WorkspaceStats;
}

// =====================================================================
// WORKSPACE MEMBER
// =====================================================================

export interface WorkspaceMember {
  id: number;
  workspace_id: number;
  user_id: number;
  permissions: WorkspacePermissions;
  created_at: string;
  updated_at: string;
  user_name?: string | null;
  user_email?: string | null;
  token_limit?: number | null;
  token_used?: number;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

// =====================================================================
// WORKSPACE GROUP
// =====================================================================

export interface WorkspaceGroup {
  id: number;
  workspace_id: number;
  name: string;
  description: string | null;
  deleted: boolean;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceGroupMember {
  id: number;
  group_id: number;
  user_id: number;
  created_at: string;
  updated_at: string;
  user_name?: string | null;
  user_email?: string | null;
  // SIMPLIFIED: Groups no longer have permissions
  // All permissions are managed at workspace level (WorkspaceMember.permissions)
}

// =====================================================================
// REQUEST/RESPONSE TYPES
// =====================================================================

export interface CreateWorkspaceRequest {
  name: string;
  monthly_token_limit?: number;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  monthly_token_limit?: number;
}

export interface AddWorkspaceMemberRequest {
  user_id: number;
  permissions: WorkspacePermissions;
}

export interface UpdateWorkspaceMemberRequest {
  permissions: WorkspacePermissions;
}

export interface CreateWorkspaceGroupRequest {
  name: string;
  description?: string;
}

export interface UpdateWorkspaceGroupRequest {
  name?: string;
  description?: string;
}

export interface AddGroupMemberRequest {
  user_id: number;
  // SIMPLIFIED: Groups no longer have permissions
  // All permissions are managed at workspace level (WorkspaceMember.permissions)
}

// =====================================================================
// CONTEXT TYPES
// =====================================================================

export interface WorkspaceContextType {
  // Current workspace state
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  isLoading: boolean;
  error: string | null;

  // Workspace operations
  loadWorkspaces: () => Promise<void>;
  createWorkspace: (data: CreateWorkspaceRequest) => Promise<Workspace>;
  updateWorkspace: (workspaceId: number, data: UpdateWorkspaceRequest) => Promise<Workspace>;
  deleteWorkspace: (workspaceId: number) => Promise<void>;
  setActiveWorkspace: (workspaceId: number | null) => Promise<void>;
  switchWorkspace: (workspaceId: number) => Promise<void>;

  // Member operations
  loadWorkspaceMembers: (workspaceId: number) => Promise<WorkspaceMember[]>;
  addWorkspaceMember: (workspaceId: number, data: AddWorkspaceMemberRequest) => Promise<WorkspaceMember>;
  updateWorkspaceMember: (workspaceId: number, userId: number, data: UpdateWorkspaceMemberRequest) => Promise<WorkspaceMember>;
  removeWorkspaceMember: (workspaceId: number, userId: number) => Promise<void>;

  // Group operations
  loadWorkspaceGroups: (workspaceId: number) => Promise<WorkspaceGroup[]>;
  createWorkspaceGroup: (workspaceId: number, data: CreateWorkspaceGroupRequest) => Promise<WorkspaceGroup>;
  updateWorkspaceGroup: (workspaceId: number, groupId: number, data: UpdateWorkspaceGroupRequest) => Promise<WorkspaceGroup>;
  deleteWorkspaceGroup: (workspaceId: number, groupId: number) => Promise<void>;

  // Group member operations
  loadGroupMembers: (workspaceId: number, groupId: number) => Promise<WorkspaceGroupMember[]>;
  addGroupMember: (workspaceId: number, groupId: number, data: AddGroupMemberRequest) => Promise<WorkspaceGroupMember>;
  removeGroupMember: (workspaceId: number, groupId: number, userId: number) => Promise<void>;

  // Utility
  clearError: () => void;
}

// =====================================================================
// PERMISSION HELPER TYPES
// =====================================================================

export type PermissionKey = keyof WorkspacePermissions;

export interface PermissionCheckResponse {
  has_permission: boolean;
  granted_by: string | null;
}
